// src/pages/AdminSetup.jsx
import React, { useState } from 'react';
import { FiUser, FiCheck, FiAlertCircle } from 'react-icons/fi';
import { setupAdminUser } from '../utils/setupAdminUser';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const AdminSetup = () => {
  const [setupStatus, setSetupStatus] = useState('idle');
  const [error, setError] = useState(null);
  const { currentUser, loadUserProfile } = useAuth();
  const navigate = useNavigate();

  const handleSetupAdmin = async () => {
    if (!currentUser) {
      toast.error('Please log in first');
      return;
    }

    try {
      setSetupStatus('running');
      setError(null);
      
      console.log('🔧 Setting up admin profile...');
      await setupAdminUser(currentUser);
      
      // Reload user profile to get the new admin permissions
      await loadUserProfile(currentUser.uid);
      
      setSetupStatus('success');
      toast.success('Admin profile created successfully!');
      
      // Redirect to admin panel after a short delay
      setTimeout(() => {
        navigate('/admin');
      }, 2000);
      
    } catch (err) {
      console.error('Admin setup failed:', err);
      setError(err.message);
      setSetupStatus('error');
      toast.error('Admin setup failed: ' + err.message);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="text-center mb-8">
            <FiUser className="h-12 w-12 text-blue-600 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Admin Profile Setup
            </h1>
            <p className="text-gray-600">
              Set up your admin profile to access HealeHub administration features
            </p>
          </div>

          {currentUser && (
            <div className="bg-blue-50 p-4 rounded-lg mb-6">
              <h3 className="font-medium text-blue-900 mb-2">Current User:</h3>
              <p className="text-blue-700">{currentUser.email}</p>
            </div>
          )}

          <div className="space-y-6">
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-2">What this will do:</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Create your user profile with admin permissions</li>
                <li>• Enable access to the Admin panel</li>
                <li>• Allow you to manage tenants and users</li>
                <li>• Set up multi-tenant system access</li>
              </ul>
            </div>

            <div className="flex justify-center">
              {setupStatus === 'idle' && (
                <button
                  onClick={handleSetupAdmin}
                  className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium"
                >
                  <FiUser className="h-4 w-4" />
                  Setup Admin Profile
                </button>
              )}
              
              {setupStatus === 'running' && (
                <div className="flex items-center gap-2 text-blue-600">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                  <span>Setting up admin profile...</span>
                </div>
              )}
              
              {setupStatus === 'success' && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-green-600 mb-2">
                    <FiCheck className="h-5 w-5" />
                    <span className="font-medium">Admin Profile Created!</span>
                  </div>
                  <p className="text-sm text-gray-600">Redirecting to admin panel...</p>
                </div>
              )}
              
              {setupStatus === 'error' && (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 text-red-600 mb-2">
                    <FiAlertCircle className="h-5 w-5" />
                    <span className="font-medium">Setup Failed</span>
                  </div>
                  <button
                    onClick={handleSetupAdmin}
                    className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 text-sm"
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-2">
                  <FiAlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-red-900">Setup Error</h4>
                    <p className="text-red-700 text-sm mt-1">{error}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSetup;
